import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useProfileByUsernameOrId } from '@/hooks/useProfile';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, FileText } from 'lucide-react';
import { LoadingState, ErrorState } from '@/components/common';
import { BioEditor } from '@/components/profiles/BioEditor';

export const ProfileBioEditPage: React.FC = () => {
  const { userName } = useParams<{ userName: string }>();
  const { data: profile, isLoading, error, refetch } = useProfileByUsernameOrId(userName);

  if (isLoading) return <LoadingState message="Loading profile..." />;
  if (error || !profile || !profile.userId) {
    return <ErrorState error={error || new Error('Profile not found')} onRetry={refetch} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/admin/profiles/${encodeURIComponent(userName || profile.userId)}`}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <FileText className="h-7 w-7" />
            Edit Bio
          </h1>
          <p className="text-muted-foreground mt-1">
            Update summary, career details, goals, and interests.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {profile.userName || profile.email || profile.userId}
          </CardTitle>
          <CardDescription>Changes are saved to the user profile service.</CardDescription>
        </CardHeader>
        <CardContent>
          <BioEditor profileId={profile.userId} bio={profile.bio} readOnly={false} />
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileBioEditPage;
